import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;

  if (!article) {
    return <NotFound />;
  }

  const recentArticles = getRecentArticles(4).filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: article.author,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          image: article.coverImage,
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - 蓝色导航发布页`,
          description: article.excerpt,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: '/' },
            { name: article.categoryName, url: `/category/${article.category}` },
            { name: article.title, url: `/article/${article.slug}` },
          ],
        }}
      />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs text-accent-600 bg-accent-100 px-3 py-1 rounded-full font-medium hover:bg-accent-200 transition-colors whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full font-medium whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="whitespace-nowrap">{article.author}</span>
                <span className="text-foreground-300">|</span>
                <span className="whitespace-nowrap">{article.publishDate}</span>
                <span className="text-foreground-300">|</span>
                <span className="whitespace-nowrap">阅读 {article.readTime}</span>
              </div>
            </div>

            <div className="aspect-[21/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 蓝色导航发布页`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {recentArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {recentArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                        {related.categoryName}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-900 mt-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                      <span className="text-xs text-foreground-400 block mt-2 whitespace-nowrap">{related.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}